import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiErrorMessage } from '../lib/api';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '' });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) {
      newErrors.email = t('errors.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth.invalidEmail');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await authService.forgotPassword(formData.email);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      setErrors({ general: getApiErrorMessage(error, t('auth.forgotPasswordError')) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <AuthShell title={t('auth.forgotPasswordTitle')} subtitle={t('auth.forgotPasswordSubtitle')}>
{isSubmitted ? (
          /* Success screen — enumeration-safe, user sees the same message whether or not the email exists */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            <div className="flex items-start gap-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-xl p-4">
              <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
              <p className="text-sm text-green-700 dark:text-green-300">
                {t('auth.forgotPasswordSuccessMessage')}
              </p>
            </div>
            <div className="text-center">
              <Link
                to="/login"
                className="font-medium text-brand-700 hover:text-brand-600"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        ) : (
          /* Form */
          <form className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.email')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-brand text-sm"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink"></div>
                ) : (
                  <>
                    <ShieldCheck className="h-5 w-5 mr-2" />
                    {t('auth.sendResetLink')}
                  </>
                )}
              </button>
            </div>

            {/* Back to login link */}
            <div className="text-center">
              <Link
                to="/login"
                className="font-medium text-brand-700 hover:text-brand-600"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </form>
        )}
    </AuthShell>
  );
};

export default ForgotPassword;
